import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/auth/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import { PageHeader, Card, Button, Select, Input, SignalBadge, Grade } from "@/components/ui-kit";
import { shareAnalysis } from "@/lib/shareAnalysis";
import { ShareButtons } from "@/components/ShareButtons";
import { toast } from "sonner";
import { UploadSimple, ImageSquare, Sparkle, X, ChartLineUp, ArrowsClockwise, ShareNetwork } from "@phosphor-icons/react";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=cz.trademind.app";

// Gentle Google Play rating prompt: from 2nd successful analysis, max once per 7 days, never after rating
const maybeAskRating = (lang) => {
  const count = parseInt(localStorage.getItem("tm_analysis_count") || "0", 10) + 1;
  localStorage.setItem("tm_analysis_count", String(count));
  if (localStorage.getItem("tm_rated")) return;
  const last = parseInt(localStorage.getItem("tm_rating_prompt_ts") || "0", 10);
  if (count < 2 || Date.now() - last < 7 * 86400000) return;
  localStorage.setItem("tm_rating_prompt_ts", String(Date.now()));
  toast(lang === "cs" ? "Pomáhá ti TradeMind AI? ⭐" : "Is TradeMind AI helping you? ⭐", {
    description: lang === "cs" ? "Ohodnoť nás na Google Play — moc nám to pomůže!" : "Rate us on Google Play — it helps a lot!",
    duration: 10000,
    action: {
      label: lang === "cs" ? "Ohodnotit" : "Rate",
      onClick: () => {
        localStorage.setItem("tm_rated", "1");
        window.open(PLAY_STORE_URL, "_blank");
      },
    },
  });
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const Analyze = () => {
  const { t, lang } = useT();
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [style, setStyle] = useState("auto");
  const [riskMode, setRiskMode] = useState(user?.risk_mode || "standard");
  const [balance, setBalance] = useState(10000);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [quota, setQuota] = useState(null);
  const fileRef = useRef();

  const fetchQuota = async () => {
    try {
      const { data } = await api.get("/usage/quota");
      setQuota(data);
    } catch { /* silent */ }
  };
  useEffect(() => { fetchQuota(); }, []);

  const onFile = async (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      toast.error("Pouze PNG, JPG nebo WEBP");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Max 10MB");
      return;
    }
    setFile(f);
    const b64 = await fileToBase64(f);
    setPreview(b64);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) await onFile(f);
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
  };

  const submit = async () => {
    if (!preview) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/analyze", {
        image_base64: preview,
        style,
        risk_mode: riskMode,
        account_balance: parseFloat(balance) || 10000,
        notes,
      });
      setResult(data.result);
      toast.success(lang === "cs" ? "Analýza hotová!" : "Analysis complete!");
      fetchQuota();
      maybeAskRating(lang);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader title={t("analyze.title")} sub={t("analyze.sub")} />
      {quota && !quota.is_admin && (
        <div className="px-4 sm:px-6 lg:px-8 pt-4" data-testid="quota-banner">
          <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-mono rounded-md border px-3 py-2 ${quota.blocked_by_cost_cap || quota.remaining_today === 0 ? "border-red-500/30 bg-red-500/5 text-red-300" : "border-zinc-800 bg-zinc-900/50 text-zinc-400"}`}>
            <span className="uppercase tracking-wider">{lang === "cs" ? "Zbývající analýzy pro dnešek" : "Analyses remaining today"}:</span>
            <span className="text-zinc-100 font-semibold" data-testid="quota-remaining">{quota.remaining_today} / {quota.daily_limit}</span>
            <span className="text-zinc-600 hidden sm:inline">·</span>
            <span className="uppercase tracking-wider text-blue-400">{quota.plan}</span>
            {quota.blocked_by_cost_cap && (
              <span className="text-red-400">{lang === "cs" ? "Měsíční AI limit vyčerpán — analýzy pozastaveny" : "Monthly AI cap reached — analyses paused"}</span>
            )}
            {!quota.blocked_by_cost_cap && quota.remaining_today === 0 && (
              <Link to="/app/upgrade" data-testid="quota-upgrade-link" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
                {lang === "cs" ? "Upgradovat plán" : "Upgrade plan"}
              </Link>
            )}
          </div>
        </div>
      )}
      <div className="p-4 sm:p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT: Upload + form */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-6">
            {!preview ? (
              <label
                onDrop={onDrop}
                onDragOver={(e) => e.preventDefault()}
                data-testid="upload-dropzone"
                className="border-2 border-dashed border-zinc-700 bg-zinc-900/40 hover:bg-zinc-900 hover:border-zinc-500 transition-all rounded-xl p-12 text-center cursor-pointer block group"
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0])}
                  data-testid="upload-input"
                />
                <UploadSimple weight="thin" size={48} className="mx-auto text-zinc-600 group-hover:text-zinc-400 transition-colors mb-3" />
                <div className="text-sm font-medium text-zinc-300">{t("analyze.drop")}</div>
                <div className="text-xs text-zinc-500 mt-1">{t("analyze.formats")}</div>
              </label>
            ) : (
              <div className="relative" data-testid="upload-preview">
                <img src={preview} alt="chart" className="rounded-md w-full" />
                <button onClick={reset} data-testid="upload-reset" className="absolute top-2 right-2 bg-zinc-950/80 hover:bg-zinc-900 border border-zinc-700 rounded-md p-1.5 text-zinc-300 hover:text-zinc-100 transition-colors">
                  <X weight="bold" size={16} />
                </button>
              </div>
            )}
          </Card>

          <Card className="p-6 space-y-4">
            <Select
              data-testid="analyze-style"
              label={t("analyze.style")}
              value={style}
              onChange={(e) => setStyle(e.target.value)}
              options={[
                { value: "auto", label: t("analyze.style.auto") },
                { value: "scalping", label: t("analyze.style.scalping") },
                { value: "intraday", label: t("analyze.style.intraday") },
                { value: "swing", label: t("analyze.style.swing") },
              ]}
            />
            <Select
              data-testid="analyze-risk"
              label={t("analyze.risk")}
              value={riskMode}
              onChange={(e) => setRiskMode(e.target.value)}
              options={[
                { value: "conservative", label: t("analyze.risk.conservative") },
                { value: "standard", label: t("analyze.risk.standard") },
                { value: "aggressive", label: t("analyze.risk.aggressive") },
              ]}
            />
            <Input data-testid="analyze-balance" type="number" label={t("analyze.balance")} value={balance} onChange={(e) => setBalance(e.target.value)} />
            <div>
              <label className="text-sm font-medium text-zinc-400 mb-1.5 block">{t("analyze.notes")}</label>
              <textarea
                data-testid="analyze-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-zinc-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none text-sm placeholder-zinc-600 resize-none"
                placeholder={lang === "cs" ? "Co tě zaujalo na grafu?" : "What caught your eye?"}
              />
            </div>
            <Button data-testid="analyze-submit" onClick={submit} disabled={!preview || loading} className="w-full flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <><ArrowsClockwise size={16} className="animate-spin" /> {t("analyze.loading")}</> : <><Sparkle weight="bold" size={16} /> {t("analyze.submit")}</>}
            </Button>
          </Card>
        </div>

        {/* RIGHT: Result */}
        <div className="lg:col-span-3 space-y-4">
          {!result && !loading && (
            <Card className="p-12 text-center text-zinc-500" data-testid="analyze-empty">
              <ImageSquare weight="thin" size={64} className="mx-auto mb-4 text-zinc-700" />
              <p className="text-sm">{lang === "cs" ? "Nahraj graf a klikni na 'Analyzovat'. AI ti vrátí detailní setup s entry, SL a TP." : "Upload a chart and click 'Analyze'. AI returns a detailed setup with entry, SL and TP."}</p>
            </Card>
          )}

          {loading && (
            <Card className="p-12 text-center" data-testid="analyze-loading">
              <div className="inline-flex items-center gap-3 text-zinc-300">
                <ArrowsClockwise size={24} className="animate-spin text-blue-400" />
                <span className="font-mono uppercase tracking-wider text-sm">{t("analyze.loading")}</span>
              </div>
            </Card>
          )}

          {result && <AnalysisResult result={result} t={t} lang={lang} />}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, accent = "zinc-100" }) => (
  <div className="flex justify-between py-2 border-b border-zinc-800 last:border-0">
    <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</span>
    <span className={`text-sm font-mono text-${accent}`}>{value}</span>
  </div>
);

const AnalysisResult = ({ result, t, lang }) => {
  const r = result;
  const [sharing, setSharing] = useState(false);

  const onShare = async () => {
    setSharing(true);
    try {
      const mode = await shareAnalysis(r, lang);
      if (mode === "downloaded") toast.success(lang === "cs" ? "Obrázek stažen — sdílej ho kdekoli!" : "Image downloaded — share it anywhere!");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(lang === "cs" ? "Sdílení selhalo" : "Share failed");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="analyze-result">
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono font-medium text-2xl tracking-tight text-zinc-100">{r.instrument || "—"}</span>
              <span className="text-xs font-mono uppercase tracking-wider text-zinc-500 border border-zinc-800 rounded px-2 py-0.5">{r.timeframe || "—"}</span>
            </div>
            <div className="text-sm text-zinc-400">{r.style ? r.style.toUpperCase() : ""} · {r.expected_duration || ""}</div>
          </div>
          <div className="flex items-center gap-3">
            <Grade grade={r.setup_grade || "C"} />
            <SignalBadge direction={r.direction || "WAIT"} />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-md p-3">
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t("analyze.result.confidence")}</div>
            <div className="text-xl font-mono font-medium text-zinc-100">{r.confidence || 0}<span className="text-zinc-500 text-sm">%</span></div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-md p-3">
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t("analyze.result.rr")}</div>
            <div className="text-xl font-mono font-medium text-zinc-100">1:{r.risk_reward || "—"}</div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-md p-3">
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Success %</div>
            <div className="text-xl font-mono font-medium text-zinc-100">{r.success_probability || 0}<span className="text-zinc-500 text-sm">%</span></div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-md p-3">
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Trend</div>
            <div className="text-sm font-mono font-medium text-zinc-100 uppercase">{r.trend || "—"}</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            onClick={onShare}
            disabled={sharing}
            data-testid="share-analysis-btn"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 transition-colors disabled:opacity-50"
          >
            <ShareNetwork weight="bold" size={16} />
            {sharing ? "..." : (lang === "cs" ? "Sdílet obrázek" : "Share image")}
          </button>
          <ShareButtons
            url="https://trademind.cz"
            text={lang === "cs"
              ? `AI analýza ${r.instrument || "grafu"}: ${(r.direction || "WAIT").toUpperCase()} s důvěrou ${r.confidence || 0} % 📊 Vyzkoušej TradeMind AI:`
              : `AI chart analysis ${r.instrument || ""}: ${(r.direction || "WAIT").toUpperCase()} at ${r.confidence || 0}% confidence 📊 Try TradeMind AI:`}
            lang={lang}
          />
        </div>
      </Card>

      <Card className="p-6">
        <h4 className="text-sm font-mono uppercase tracking-wider text-zinc-500 mb-3">{lang === "cs" ? "Cenové úrovně" : "Price levels"}</h4>
        <Row label={t("analyze.result.entry")} value={r.entry || "—"} />
        <Row label={t("analyze.result.sl")} value={r.stop_loss || "—"} accent="red-400" />
        <Row label={t("analyze.result.tp1")} value={r.take_profit_1 || "—"} accent="green-400" />
        <Row label={t("analyze.result.tp2")} value={r.take_profit_2 || "—"} accent="green-400" />
        <Row label={t("analyze.result.tp3")} value={r.take_profit_3 || "—"} accent="green-400" />
      </Card>

      <Card className="p-6">
        <h4 className="text-sm font-mono uppercase tracking-wider text-zinc-500 mb-2">{t("analyze.result.reasoning")}</h4>
        <p className="text-sm text-zinc-200 leading-relaxed">{r.reasoning || "—"}</p>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {r.strengths?.length > 0 && (
          <Card className="p-6">
            <h4 className="text-sm font-mono uppercase tracking-wider text-green-400 mb-3">{t("analyze.result.strengths")}</h4>
            <ul className="space-y-2">
              {r.strengths.map((s, i) => <li key={i} className="text-sm text-zinc-300 flex gap-2"><span className="text-green-500">+</span>{s}</li>)}
            </ul>
          </Card>
        )}
        {r.weaknesses?.length > 0 && (
          <Card className="p-6">
            <h4 className="text-sm font-mono uppercase tracking-wider text-orange-400 mb-3">{t("analyze.result.weaknesses")}</h4>
            <ul className="space-y-2">
              {r.weaknesses.map((s, i) => <li key={i} className="text-sm text-zinc-300 flex gap-2"><span className="text-orange-500">!</span>{s}</li>)}
            </ul>
          </Card>
        )}
      </div>

      {r.invalidation && (
        <Card className="p-6 border-red-500/20 bg-red-500/5">
          <h4 className="text-sm font-mono uppercase tracking-wider text-red-400 mb-2">{t("analyze.result.invalidation")}</h4>
          <p className="text-sm text-zinc-200">{r.invalidation}</p>
        </Card>
      )}

      {r.position_management && (
        <Card className="p-6">
          <h4 className="text-sm font-mono uppercase tracking-wider text-blue-400 mb-2">{t("analyze.result.management")}</h4>
          <p className="text-sm text-zinc-200">{r.position_management}</p>
        </Card>
      )}
    </div>
  );
};

export default Analyze;
